// useScans.ts
import { useEffect, useMemo, useState } from 'react'
import { format } from 'date-fns'
import { getAllScanAsync, getScanByIdAsync } from '../../../../../redux/flows/portal/currentPatient'
import { useAppDispatch, useAppSelector } from '../../../../../redux/store'

type APIStatus = 'idle' | 'pending' | 'fulfilled' | 'rejected'

function convertUtcToEst(d: Date) {
  return d
}

export const useScans = () => {
  const dispatch = useAppDispatch()

  const currentPatient = useAppSelector((s) => s.currentPatientPortal.currentPatient)

  const scansList = useAppSelector((s) => s.currentPatientPortal.scans)
  const scansListStatus = useAppSelector((s) => s.currentPatientPortal.scansStatus)

  const scanDetail = useAppSelector((s) => s.currentPatientPortal.scanById)
  const scanDetailStatus = useAppSelector((s) => s.currentPatientPortal.scanByIdStatus)

  const [scans, setScans] = useState<any | null>(null)
  const [isScanEmpty, setIsScanEmpty] = useState(false)
  const [scansStatus, setScansStatus] = useState<APIStatus>('idle')
  const [scansByIdStatus, setScansByIdStatus] = useState<APIStatus>('idle')
  const [scansById, setScansById] = useState<any | null>(null)

  const [scanReport, setScanReport]: any = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(0)
  const [lastPageIndex, setLastPageIndex] = useState(-1)
  const [processFinished, setProcessFinished] = useState(false)

  // =====================================================
  // MODE A ONLY — newest → oldest
  // =====================================================
  const buildUserScanList = (apiScans: any[]) => {
    if (!apiScans || apiScans.length === 0) return []

    return apiScans
      .map((s) => ({
        id: s.id,
        appointmentTime: s.appointmentTime ?? s.createdAt ?? s.updatedAt,
      }))
      .sort((a, b) => new Date(b.appointmentTime).getTime() - new Date(a.appointmentTime).getTime())
  }

  const injectCurrentPatientScan = (list: any[]) => {
    if (!currentPatient?.scanData || !currentPatient?.spinalSemgScan?.length) {
      return list
    }

    const thisId = currentPatient.scanData.id
    const exists = list.some((x) => x.id === thisId)
    if (exists) return list

    return [
      {
        id: thisId,
        appointmentTime: currentPatient.scanData.createdAt,
      },
      ...list,
    ]
  }

  // FETCH ALL SCANS
  useEffect(() => {
    if (!currentPatient?.patient?.id) return
    dispatch(getAllScanAsync({ patientId: currentPatient.patient.id }))
  }, [currentPatient?.patient?.id, dispatch])

  // SYNC getAllScan
  useEffect(() => {
    if (scansListStatus === 'pending') {
      setIsLoading(true)
      setScansStatus('pending')
      return
    }

    if (scansListStatus === 'fulfilled') {
      const apiList = buildUserScanList((scansList as any)?.userScan || [])
      const merged = injectCurrentPatientScan(apiList)

      if (merged.length === 0) {
        setIsScanEmpty(true)
      } else {
        setScans({
          userScan: merged,
          firstScan: { id: merged[merged.length - 1]?.id },
        })
      }

      setLastPageIndex(merged.length - 1)
      setScansStatus('fulfilled')
      setIsLoading(false)
    }

    if (scansListStatus === 'rejected') {
      setIsLoading(false)
      setScansStatus('rejected')
      setIsScanEmpty(true)
    }
  }, [scansListStatus, scansList, currentPatient])

  // FETCH scanById ON PAGE CHANGE
  useEffect(() => {
    if (scansStatus !== 'fulfilled') return
    if (isScanEmpty) return
    if (!scans?.userScan?.length) return
    if (!currentPatient?.patient?.id) return

    const scanId = scans.userScan[currentPage]?.id
    if (!scanId) return

    setScansByIdStatus('pending')

    dispatch(
      getScanByIdAsync({
        patientId: currentPatient.patient.id,
        scanId: scanId,
      }),
    )
  }, [currentPage, scansStatus, scans, isScanEmpty, currentPatient?.patient?.id, dispatch])

  // SYNC scanById
  useEffect(() => {
    if (scanDetailStatus === 'fulfilled') {
      setScansById(scanDetail)
      setScansByIdStatus('fulfilled')
    }
    if (scanDetailStatus === 'rejected') {
      setScansByIdStatus('rejected')
      setScansById(null)
    }
    if (scanDetailStatus === 'pending') {
      setScansByIdStatus('pending')
    }
  }, [scanDetailStatus, scanDetail])

  // =====================================================
  // BUILD scanReport
  // =====================================================
  useEffect(() => {
    if (scansByIdStatus !== 'fulfilled' || !scansById?.userScanDatas) {
      if (scansByIdStatus !== 'fulfilled') setProcessFinished(false)
      return
    }

    const desiredOrder = ['c2', 'c4', 'c6', 't1', 't3', 't5', 't7', 't9', 't11', 'l1', 'l3', 'l5']

    // Normalize existing
    const normalized = scansById.userScanDatas.map((d: any) => ({
      ...d,
      spineLevel: d.spineLevel?.toLowerCase(),
    }))

    // Detect missing spine levels
    const existingLevels = new Set(normalized.map((d: any) => d.spineLevel))
    const missingLevels = desiredOrder.filter((lvl: any) => !existingLevels.has(lvl))

    // Fillers for missing
    const fillers = missingLevels.map((lvl) => ({
      spineLevel: lvl,
      leftValue: 0,
      rightValue: 0,
      userScanId: scansById?.id,
      __isMissing: true,
    }))

    // Merge existing + fillers
    const completedList = [...normalized, ...fillers]

    // Final ordered list
    const ordered = completedList.sort(
      (a: any, b: any) => desiredOrder.indexOf(a.spineLevel) - desiredOrder.indexOf(b.spineLevel),
    )

    let highestLeftValue = 0
    let highestRightValue = 0

    ordered.forEach((obj: any) => {
      if (obj.leftValue > highestLeftValue) highestLeftValue = obj.leftValue
      if (obj.rightValue > highestRightValue) highestRightValue = obj.rightValue
    })

    const maxValue = Math.max(highestLeftValue, highestRightValue)

    const range: any = {
      c2: { min: 2.0, good: 3.0, high: 7.0 },
      c4: { min: 2.0, good: 3.0, high: 7.0 },
      c6: { min: 2.0, good: 3.0, high: 8.0 },
      t1: { min: 2.0, good: 3.0, high: 8.3 },
      t3: { min: 3.5, good: 5.5, high: 7.0 },
      t5: { min: 3.5, good: 5.5, hi: 7.0 },
      t7: { min: 3.5, good: 5.5, high: 7.0 },
      t9: { min: 3.0, good: 5.0, high: 7.0 },
      t11: { min: 3.0, good: 5.0, high: 7.0 },
      l1: { min: 4.3, good: 6.3, high: 9.4 },
      l3: { min: 4.1, good: 7.1, high: 9.5 },
      l5: { min: 4.3, good: 7.3, high: 9.4 },
    }

    const getBarWidth = (n: number) => {
      const FIRST_SCALE = 0.6
      let scaleFactor = 0.0
      let percent = 0.0
      if (n <= 20) {
        percent = n / 20.0
        scaleFactor = percent * FIRST_SCALE
      } else if (n > 20 && n <= 150) {
        percent = n / 200.0
        scaleFactor = 0.5 + percent * (1 - FIRST_SCALE)
      } else if (n > 150) {
        scaleFactor = 0.8
      }
      return scaleFactor
    }

    const processed = ordered.map((item: any) => {
      const { spineLevel, leftValue, rightValue, userScanId } = item
      const isMissing = item.__isMissing === true
      const isFirstScan = currentPage === lastPageIndex

      const getBarColor = (value: any, spine: any) => {
        if (value > 0 && value <= spine.min) return '#D9D5D2'
        if (value > spine.min && value < spine.good) return '#007F7C'
        if (value >= spine.good && value < spine.high) return '#D9D5D2'
        if (value >= spine.high) return '#000000'
        return '#000000'
      }

      const leftBarColor = range[spineLevel] ? getBarColor(leftValue, range[spineLevel]) : '#000'
      const rightBarColor = range[spineLevel] ? getBarColor(rightValue, range[spineLevel]) : '#000'

      let leftTextPercent = rightValue === 0 ? 100 : Math.round((leftValue / rightValue - 1) * 100)
      let rightTextPercent = leftValue === 0 ? 100 : Math.round((rightValue / leftValue - 1) * 100)

      let isBothEqual = leftValue === rightValue
      let showLeftText = leftValue > rightValue
      let showRightText = rightValue > leftValue
      let hideText = false

      const leftBarWidth = getBarWidth(leftValue) * 100 * 2
      const rightBarWidth = getBarWidth(rightValue) * 100 * 2

      const leftBarPxLength = leftTextPercent.toString().length
      const rightBarPxLength = rightTextPercent.toString().length

      const barToShowLength = leftTextPercent > rightTextPercent ? leftBarPxLength : rightBarPxLength
      const barWidth = leftValue > rightValue ? leftBarWidth : rightBarWidth

      if (barToShowLength === 1 && barWidth < 59) hideText = true
      else if (barToShowLength === 2 && barWidth < 66.5) hideText = true
      else if (barToShowLength === 3 && barWidth < 74) hideText = true
      else if (barToShowLength >= 4 && barWidth < 81.5) hideText = true
      else if (barToShowLength >= 5) hideText = true

      // DOT LOGIC
      const dotInfo: any = {
        leftDotPosition: null,
        rightDotPosition: null,
        shouldShowLeftDot: true,
        shouldShowRightDot: true,
      }

      if (scansById.fistSpinalSemgScan) {
        const first = scansById.fistSpinalSemgScan.map((d: any) => ({
          ...d,
          spineLevel: d.spineLevel.toLowerCase(),
        }))

        const orderedFirst = first.sort(
          (a: any, b: any) => desiredOrder.indexOf(a.spineLevel) - desiredOrder.indexOf(b.spineLevel),
        )

        orderedFirst.forEach((fs: any) => {
          if (fs.spineLevel === spineLevel) {
            const leftDotW = Math.min(getBarWidth(fs.leftValue) * 100 * 2, 195)
            const rightDotW = Math.min(getBarWidth(fs.rightValue) * 100 * 2, 195)

            dotInfo.leftDotPosition = leftDotW
            dotInfo.rightDotPosition = rightDotW

            if (leftDotW < leftBarWidth) dotInfo.shouldShowLeftDot = false
            if (rightDotW < rightBarWidth) dotInfo.shouldShowRightDot = false
          }
        })
      }

      // ------------------------------
      // HIDE DOTS if this is FIRST SCAN + missing row
      // ------------------------------
      if (isMissing && isFirstScan) {
        dotInfo.shouldShowLeftDot = false
        dotInfo.shouldShowRightDot = false
        dotInfo.leftDotPosition = null
        dotInfo.rightDotPosition = null
      }

      return {
        spineLevel,
        leftTextPercent,
        rightTextPercent,
        showLeftText,
        showRightText,
        isBothEqual,
        leftBarWidth,
        rightBarWidth,
        leftBarColor,
        rightBarColor,
        hideText,
        leftValue,
        rightValue,
        userScanId,
        dotInfo,
      }
    })

    setScanReport(processed)
    setProcessFinished(true)
  }, [scansByIdStatus, scansById])

  // DATE MEMO
  const scanDate = useMemo(() => {
    const date = scansById?.appointmentTime ? convertUtcToEst(new Date(scansById.appointmentTime)) : null
    return {
      day: date ? format(date, 'MMMM d') : null,
      studioName: scansById?.clinic?.name,
    }
  }, [scansById])

  const handlePreviousScan = () => {
    if (currentPage < lastPageIndex) {
      setCurrentPage((p) => p + 1)
      setProcessFinished(false)
      setScansByIdStatus('pending')
    }
  }

  const handleNextScan = () => {
    if (currentPage > 0) {
      setCurrentPage((p) => p - 1)
      setProcessFinished(false)
      setScansByIdStatus('pending')
    }
  }

  const defaultScanReport = useMemo(
    () => [
      { spineLevel: 'C2', value: 55 },
      { spineLevel: 'C4', value: 55 },
      { spineLevel: 'C6', value: 55 },
      { spineLevel: 'T1', value: 55 },
      { spineLevel: 'T3', value: 75 },
      { spineLevel: 'T5', value: 75 },
      { spineLevel: 'T7', value: 75 },
      { spineLevel: 'T9', value: 75 },
      { spineLevel: 'T11', value: 105 },
      { spineLevel: 'L1', value: 105 },
      { spineLevel: 'L3', value: 105 },
      { spineLevel: 'L5', value: 105 },
    ],
    [],
  )

  return {
    scans,
    isScanEmpty,
    scansStatus,
    scansByIdStatus,
    scansById,
    scanReport,
    isLoading,
    currentPage,
    lastPageIndex,
    processFinished,
    scanDate,
    defaultScanReport,
    handlePreviousScan,
    handleNextScan,
  }
}
